import React from "react";
import {
  DropdownInput,
  Input,
  InputLabelStyleTypes,
  IOption,
  QuestionTooltip,
  QuestionTooltipProps,
  RadioList,
} from "component-library";
import { ControlSection, InfoSection } from "../../../../../Common";
import {
  FacetCategoryViewModel,
  FacetViewModel,
  IGenderViewModel,
} from "../../../../models/views";

interface IGenderOptionContent {
  warning: string;
  only: string;
  female: string;
  femaleInfo: string;
  male: string;
  maleInfo: string;
  femaleAndMale: string;
  femaleAndMaleInfo: string;
}

export interface IGenderContent {
  genderOptionLabel: string;
  genderSectionName: string;
  genderField: string;
  genderTooltip: QuestionTooltipProps;
  genderFurtherInfoField: string;
  genderShowOnProfileTogglerName: string;
  genderShowOnProfileTogglerNegative: string;
  genderShowOnProfileTogglerNegativeDescription: string;
  genderShowOnProfileTogglerPositive: string;
  genderShowOnProfileTogglerPositiveDescription: string;
  genderCustom: IGenderOptionContent;
  genderNonBinary: IGenderOptionContent;
  genderTransgenderMaleInfo: string;
  genderTransgenderFemaleInfo: string;
}

interface IGenderProps {
  model: IGenderViewModel;
  facet: FacetCategoryViewModel;
  content: IGenderContent;
  handleChanges: (model: IGenderViewModel) => void;
}

const GENDER_INFO_MAX_LENGTH = 75;

const GENDER_FACET_CODES = {
  female: "Gender_Other_Female",
  male: "Gender_Other_Male",
  transgenderFemale: "Gender_Other_Transgenderfemale",
  transgenderMale: "Gender_Other_Transgendermale",
  nonBinary: "Gender_Other_NonBinary",
  custom: "Gender_Other_Custom",
};

enum GenderCombinations {
  only = 1,
  female,
  male,
  both,
}

const FACET_ORDER = [
  GENDER_FACET_CODES.female,
  GENDER_FACET_CODES.male,
  GENDER_FACET_CODES.transgenderFemale,
  GENDER_FACET_CODES.transgenderMale,
  GENDER_FACET_CODES.nonBinary,
  GENDER_FACET_CODES.custom,
];
const CUSTOMIZABLE_GENDERS = [
  GENDER_FACET_CODES.custom,
  GENDER_FACET_CODES.nonBinary,
];
const TRANSGENDER_GENDERS = [
  GENDER_FACET_CODES.transgenderMale,
  GENDER_FACET_CODES.transgenderFemale,
];

export default class Gender extends React.Component<IGenderProps> {
  public render() {
    const { model, content } = this.props;
    const options = this.getShowGenderRadioOptions();

    const publicationOptionInfo = model.isVisibleGender
      ? content.genderShowOnProfileTogglerPositiveDescription
      : content.genderShowOnProfileTogglerNegativeDescription;

    return (
      <ControlSection name={content.genderSectionName}>
        <div className="c-basic-info-form__gender">
          <div className="c-basic-info-form__height-foot">
            {this.renderDropdown()}
          </div>
          <div className="c-basic-info-form__height-inch">
            {this.renderInfoInput()}
          </div>
        </div>
        {this.renderGenderDetails()}
        {this.renderInfoMessage()}
        <RadioList<boolean>
          name="isVisibleGender"
          value={model.isVisibleGender ?? false}
          options={options}
          labelStyleType={InputLabelStyleTypes.Secondary}
          isWidthLimited={true}
          label={content.genderShowOnProfileTogglerName}
          valueChanged={this.handleShowGenderChange}
          vertical={true}
        />
        {this.renderInfoSection(publicationOptionInfo)}
      </ControlSection>
    );
  }

  private renderDropdown = () => {
    const {
      content,
      facet: { facets },
    } = this.props;
    const options = this.getGenderDropdownOptions(facets);
    const annex = content.genderTooltip && this.getGenderDropdownAnnex();

    const [selectedFacet] = this.getGenderSelectedOption(facets);

    return (
      <DropdownInput<number>
        id="gender"
        variant="select"
        options={options}
        selected={selectedFacet}
        name="gender"
        label={content.genderField}
        annex={annex}
        optionSelected={this.handleGenderChanges}
      />
    );
  };

  private renderGenderDetails = () => {
    const {
      facet: { facets },
      content,
    } = this.props;
    const genderDetail = this.getGenderDetailsSelectedOption(facets);

    const [, genderFacet] = this.getGenderSelectedOption(facets);

    let optionContent: IGenderOptionContent = content.genderCustom;

    if (genderFacet && genderFacet.code === GENDER_FACET_CODES.nonBinary) {
      optionContent = content.genderNonBinary;
    }

    return (
      genderDetail !== undefined && (
        <RadioList<GenderCombinations>
          options={[
            {
              name: optionContent.only,
              value: GenderCombinations.only,
            },
            {
              name: optionContent.femaleAndMale,
              value: GenderCombinations.both,
            },
            {
              name: optionContent.female,
              value: GenderCombinations.female,
            },
            {
              name: optionContent.male,
              value: GenderCombinations.male,
            },
          ]}
          value={genderDetail}
          labelStyleType={InputLabelStyleTypes.Secondary}
          label={content.genderOptionLabel}
          valueChanged={this.handleGenderDetailsChanges}
          vertical={true}
        />
      )
    );
  };

  private renderInfoInput = () => {
    return (
      <Input
        id="genderInfo"
        name="genderInfo"
        type="text"
        label={this.props.content.genderFurtherInfoField}
        value={this.props.model.genderInfo ?? ""}
        maxLength={GENDER_INFO_MAX_LENGTH}
        valueChanged={this.handleGenderInfoChange}
      />
    );
  };

  private renderInfoSection = (content: string, isWarning: boolean = false) => (
    <div className="c-basic-info-form__gender-info">
      <InfoSection isWarning={isWarning}>
        <span dangerouslySetInnerHTML={{ __html: content }} />
      </InfoSection>
    </div>
  );

  private renderInfoMessage = () => {
    const {
      facet: { facets },
      content,
    } = this.props;

    let infoText = "";
    let isWarning = false;

    const genderCombination = this.getGenderDetailsSelectedOption(facets);

    const [, genderFacet] = this.getGenderSelectedOption(facets);

    if (genderFacet) {
      if (
        genderCombination &&
        CUSTOMIZABLE_GENDERS.some(
          (genderCode) => genderFacet.code === genderCode
        )
      ) {
        let optionsContent =
          genderFacet.code === GENDER_FACET_CODES.custom
            ? content.genderCustom
            : content.genderNonBinary;

        switch (genderCombination) {
          case GenderCombinations.only:
            infoText = optionsContent.warning;
            isWarning = true;
            break;
          case GenderCombinations.female:
            infoText = optionsContent.femaleInfo;
            break;
          case GenderCombinations.male:
            infoText = optionsContent.maleInfo;
            break;
          case GenderCombinations.both:
            infoText = optionsContent.femaleAndMaleInfo;
            break;
        }
      } else if (genderFacet.code === GENDER_FACET_CODES.transgenderMale) {
        infoText = content.genderTransgenderMaleInfo;
      } else if (genderFacet.code === GENDER_FACET_CODES.transgenderFemale) {
        infoText = content.genderTransgenderFemaleInfo;
      }
    }

    return (
      infoText && (
        <div className="c-basic-info-form__gender-details">
          {this.renderInfoSection(infoText, isWarning)}
        </div>
      )
    );
  };

  private handleGenderChanges = (option?: IOption<number>) => {
    const {
      facet: { facets },
    } = this.props;
    let genders: Array<{ id: number; name: string }> = [];

    if (option) {
      const facet = facets.find((facet) => facet.id === option.value);

      genders.push({ id: option.value, name: option.name });

      if (facet && TRANSGENDER_GENDERS.includes(facet.code)) {
        let additionalGender: FacetViewModel | undefined;

        if (facet.code === GENDER_FACET_CODES.transgenderFemale) {
          additionalGender = facets.find(
            (facet) => facet.code === GENDER_FACET_CODES.female
          );
        } else {
          additionalGender = facets.find(
            (facet) => facet.code === GENDER_FACET_CODES.male
          );
        }

        if (additionalGender) {
          genders.push(additionalGender);
        }
      }
    }

    this.props.handleChanges({ ...this.props.model, genders });
  };

  private handleGenderDetailsChanges = (
    genderCombination: GenderCombinations
  ) => {
    const {
      facet: { facets },
    } = this.props;

    let genders: Array<{ id: number; name: string } | undefined> = [];

    const [, genderFacet] = this.getGenderSelectedOption(facets);

    if (genderFacet) {
      genders.push({ id: genderFacet.id, name: genderFacet.name });
    }

    switch (genderCombination) {
      case GenderCombinations.female:
        genders.push(
          facets.find((facet) => facet.code === GENDER_FACET_CODES.female)
        );
        break;
      case GenderCombinations.male:
        genders.push(
          facets.find((facet) => facet.code === GENDER_FACET_CODES.male)
        );
        break;
      case GenderCombinations.both:
        genders.push(
          facets.find((facet) => facet.code === GENDER_FACET_CODES.female)
        );
        genders.push(
          facets.find((facet) => facet.code === GENDER_FACET_CODES.male)
        );
        break;
    }

    this.props.handleChanges({
      ...this.props.model,
      genders: genders.filter(Boolean) as Array<{ id: number; name: string }>,
    });
  };

  private handleGenderInfoChange = (value: string) =>
    this.props.handleChanges({ ...this.props.model, genderInfo: value });

  private handleShowGenderChange = (value: boolean) =>
    this.props.handleChanges({ ...this.props.model, isVisibleGender: value });

  private getGenderSelectedOption = (
    facets: FacetViewModel[]
  ): [IOption<number> | undefined, FacetViewModel | undefined] => {
    const {
      model: { genders },
    } = this.props;

    const selectedFacets = facets.filter((facet) =>
      genders.some((gender) => gender.id === facet.id)
    );

    let genderFacet: FacetViewModel | undefined = selectedFacets[0];

    if (
      selectedFacets.some((facet) =>
        CUSTOMIZABLE_GENDERS.some((genderCode) => facet.code === genderCode)
      )
    ) {
      genderFacet = selectedFacets.find(
        (facet) => facet.code === GENDER_FACET_CODES.custom
      );

      if (!genderFacet) {
        genderFacet = selectedFacets.find(
          (facet) => facet.code === GENDER_FACET_CODES.nonBinary
        );
      }
    } else if (
      selectedFacets.some((facet) =>
        TRANSGENDER_GENDERS.some((genderCode) => facet.code === genderCode)
      )
    ) {
      genderFacet = selectedFacets.find(
        (facet) => facet.code === GENDER_FACET_CODES.transgenderMale
      );

      if (!genderFacet) {
        genderFacet = selectedFacets.find(
          (facet) => facet.code === GENDER_FACET_CODES.transgenderFemale
        );
      }
    }

    return genderFacet
      ? [{ value: genderFacet.id, name: genderFacet.name }, genderFacet]
      : [undefined, genderFacet];
  };

  private getGenderDetailsSelectedOption = (
    facets: FacetViewModel[]
  ): GenderCombinations | undefined => {
    const {
      model: { genders },
    } = this.props;

    const selectedFacets = facets.filter((facet) =>
      genders.some((gender) => gender.id === facet.id)
    );

    let option: GenderCombinations | undefined = undefined;

    const isCustomizable = selectedFacets.some((facet) =>
      CUSTOMIZABLE_GENDERS.some((genderCode) => facet.code === genderCode)
    );

    if (isCustomizable) {
      if (
        selectedFacets.some(
          (facet) => facet.code === GENDER_FACET_CODES.male
        ) &&
        selectedFacets.some((facet) => facet.code === GENDER_FACET_CODES.female)
      ) {
        option = GenderCombinations.both;
      } else if (
        selectedFacets.some((facet) => facet.code === GENDER_FACET_CODES.female)
      ) {
        option = GenderCombinations.female;
      } else if (
        selectedFacets.some((facet) => facet.code === GENDER_FACET_CODES.male)
      ) {
        option = GenderCombinations.male;
      } else {
        option = GenderCombinations.only;
      }
    }

    return option;
  };

  private getGenderDropdownOptions = (
    facets: FacetViewModel[]
  ): IOption<number>[] =>
    facets
      .sort(
        (prev, next) =>
          FACET_ORDER.indexOf(prev.code) - FACET_ORDER.indexOf(next.code)
      )
      .map(({ name, id }) => ({ name, value: id }));

  private getGenderDropdownAnnex = () => {
    const { content } = this.props;

    return (
      <QuestionTooltip
        title={content.genderTooltip.title}
        html={content.genderTooltip?.content as string}
      />
    );
  };

  private getShowGenderRadioOptions = () => {
    const { content } = this.props;

    return [
      {
        name: content.genderShowOnProfileTogglerNegative,
        value: false,
      },
      {
        name: content.genderShowOnProfileTogglerPositive,
        value: true,
      },
    ];
  };
}
